import { ServerSettings } from '../serverSettings';
import { ErrorCodes, InformationCodes } from './index';
import { Browser } from '../browser';
import { ClientError } from './clientError';
import { HttpClient } from '../httpClient/index';
import { routes } from '../routes';
import { LoggingService, ApiModels } from './loggingService';

declare global {
    var html2canvas: (element: HTMLElement) => Promise<HTMLCanvasElement>;
}

let hasBoundGlobalErrorEvent = false;
enum SeverityTypes {
    Information = 1,
    Error = 2,
}

export class Log {
    private readonly browser: Browser;
    private readonly debug: boolean;
    private readonly httpClient: HttpClient;
    private readonly loggingService: LoggingService;

    constructor(serverSettings: ServerSettings) {
        this.browser = new Browser();
        this.debug = serverSettings.debug;
        this.httpClient = new HttpClient(serverSettings);
        this.loggingService = new LoggingService(serverSettings);
        if (!hasBoundGlobalErrorEvent) {
            hasBoundGlobalErrorEvent = true;
            window.onerror = this.handleError.bind(this);
        }
    }

    public async exception(e: any) {
        if (this.debug) {
            console.error(e);
        }
        var code: ErrorCodes = 'UnexpectedException';
        var message = this.formatException(e);
        if (e instanceof ClientError) {
            code = e.code;
            message = e.message;
        }
        const request: ApiModels.LogErrorApiRequestModel = {
            code: code,
            severityTypeId: SeverityTypes.Error,
            message: message,
            ...this.getBaseRequestProperties(),
            logId: this.getLogId(e),
            image: await this.screenshot(),
        };
        await this.loggingService.log(request);
    }

    private formatException(e: any) {
        if (e.message && e.stack && e.name) {
            return `
${e.name}
${e.message}
${e.stack}
            `;
        }
        return JSON.stringify(e, null, 2);
    }

    private getLogId(e: any) {
        if (e?.status === 500 && e?.responseJSON?.logId) {
            return e.responseJSON.logId;
        }
        return null;
    }

    public async error(errorCode: ErrorCodes, message: string) {
        if (this.debug) {
            console.error(errorCode, message);
        }
        const request: ApiModels.LogErrorApiRequestModel = {
            code: errorCode,
            severityTypeId: SeverityTypes.Error,
            message: message,
            ...this.getBaseRequestProperties(),
            image: await this.screenshot()
        };
        await this.httpClient.post(routes.api.logging.logError, request);
        await this.loggingService.log(request);
    }

    public async information(informationCode: InformationCodes, message: string) {
        if (this.debug) {
            console.log(informationCode, message);
        }
        const request: ApiModels.LogErrorApiRequestModel = {
            code: informationCode,
            severityTypeId: SeverityTypes.Information,
            message: message,
            ...this.getBaseRequestProperties(),
            image: await this.screenshot()
        };
        await this.loggingService.log(request);
    }

    private handleError = async (event: Event | string, source?: string, lineno?: number, colno?: number, error?: Error) => {
        if (this.debug) {
            console.error(event, source, lineno, colno, error);
        }
        let message: string;
        if (typeof error === 'string') {
            message = error;
        } else {
            message = error.message;
        }
        const request: ApiModels.LogErrorApiRequestModel = {
            code: 'UnexpectedException',
            severityTypeId: SeverityTypes.Error,
            message: message,
            stackTrace: error.stack,
            lineNo: lineno,
            columnNo: colno,
            ...this.getBaseRequestProperties(),
            image: await this.screenshot()
        }
        await this.loggingService.log(request);
    }

    private getBaseRequestProperties() {
        return {
            url: this.browser.location(),
            userAgent: this.browser.userAgent(),
            cookieEnabled: this.browser.isCookiesEnabled(),
            language: this.browser.language()
        }
    }

    private async screenshot() {
        var canvas = await html2canvas(document.body);
        return canvas.toDataURL('image/png');
    }
}